import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// =============================================================================
// svc-04-admin-config `GET /v1/admin/tasks/:taskId` — eager-loaded read for
// the Admin Edit-Task modal. One call, every section. Backed by
// `apps/svc-04-admin-config/src/admin-tasks/*` and
// `packages/shared-types/src/svc-04-admin-config/v1/admin-task-detail.schema.ts`.
//
// `comments` is `null` while there is no `task_comments` table in
// `penn_config` (operator-locked 2026-06-11 — DB stays untouched). Treat that
// as "comments not available", not "0 comments".
// =============================================================================

export interface AdminTaskDetailPersonRef {
  email: string | null;
  name: string | null;
  role: string | null;
}

export interface AdminTaskDetailRequestedBy {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: string | null;
  company: { name: string | null; address: string | null };
}

export interface AdminTaskDetailWorkingFor {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  company: { name: string | null; address: string | null };
}

export interface AdminTaskDetailWorkflowStep {
  status: string | null;
  doneAt: string | null;
  doneBy: AdminTaskDetailPersonRef | null;
}

export interface AdminTaskDetailWorkflow {
  engineeringReview: AdminTaskDetailWorkflowStep;
  quote: AdminTaskDetailWorkflowStep;
}

export interface AdminTaskDetailComment {
  id: string;
  comment: string;
  createdAt: string | null;
  createdBy: AdminTaskDetailPersonRef | null;
}

export interface AdminTaskDetailSelectedOption {
  partNo: string;
  description: string | null;
  category: string | null;
  toolingType: string | null;
  machineType: string | null;
  qty: number | null;
  price: number | null;
  itemName: string | null;
  partName: string | null;
}

export interface AdminTaskDetailApplicationFastener {
  brand: string | null;
  type: string | null;
  qtyPerPart: number | null;
  qtyPerPartA: number | null;
  qtyPerPartB: number | null;
}

export interface AdminTaskDetailApplication {
  id: string;
  name: string | null;
  applicationNumber: string | null;
  oem: string | null;
  productionProgram: string | null;
  material: string | null;
  annualVolume: number | null;
  batchSize: number | null;
  fasteners: AdminTaskDetailApplicationFastener[];
}

export interface AdminTaskDetailFilters {
  view: string | null;
  region: string | null;
  types: string[];
  size: string | null;
  pm: boolean | null;
  ri: boolean | null;
  forceMinKN: number | null;
  forceMaxKN: number | null;
  compareIds: string[];
}

export interface AdminTaskDetailSnapshot {
  source: string | null;
  createdAtClient: string | null;
  solution: {
    productId: string | null;
    solutionName: string | null;
    machineType: string | null;
  };
  selections: {
    selectedPartNos: string[];
    selectedOptions: AdminTaskDetailSelectedOption[];
    optionsTotal: number | null;
  };
  applications: AdminTaskDetailApplication[];
  filters: AdminTaskDetailFilters;
}

export interface AdminTaskDetailManualLineItem {
  id: string;
  isManual: boolean | null;
  partNo: string | null;
  description: string | null;
  category: string | null;
  toolingType: string | null;
  machineType: string | null;
  itemName: string | null;
  partName: string | null;
  currency: string | null;
  price: number | null;
  qty: number | null;
  createdAt: string | null;
  createdBy: AdminTaskDetailPersonRef | null;
}

export interface AdminTaskDetail {
  id: string;
  status: string | null;
  flowType: string | null;
  machineCostMode: string | null;
  comment: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  requestedBy: AdminTaskDetailRequestedBy;
  workingFor: AdminTaskDetailWorkingFor | null;
  workflow: AdminTaskDetailWorkflow;
  comments: AdminTaskDetailComment[] | null;
  snapshot: AdminTaskDetailSnapshot | null;
  manualLineItems: AdminTaskDetailManualLineItem[];
  quotedAt: string | null;
  quotedBy: { email: string | null; name: string | null } | null;
}

export async function getAdminTaskDetail(taskId: string): Promise<AdminTaskDetail> {
  const res = await Api.get(`/v1/admin/tasks/${encodeURIComponent(taskId)}`);
  return unwrap<AdminTaskDetail>(res.data);
}

// POST /v1/admin/tasks/:taskId/comments — append one comment to the thread.
// Author is taken from the JWT server-side. Returns the created comment.
export async function addAdminTaskComment(
  taskId: string,
  comment: string,
): Promise<AdminTaskDetailComment> {
  const res = await Api.post(
    `/v1/admin/tasks/${encodeURIComponent(taskId)}/comments`,
    { comment },
  );
  return unwrap<AdminTaskDetailComment>(res.data);
}
